import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject, computed, resource } from "@angular/core";
import { Subject, firstValueFrom } from "rxjs";
import { IUserDashboardSettingsModel, TUserDashboardSettingsModels } from "../../views-interfaces/user-dashboard-settings.interface";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import type {
    IUserDashboardSettingsMapByUserId,
    IUserDashboardSettingsMapByUserAndDashboardId,
    IuserDashboardSettingsMapById,
    IUserDashboardSettingsMapByDashboardId,
    IUserDashboardSettingsForMe,
    IUserDashboardSettingsMapByDashboard,
} from "@interfaces/index";
import type {
    IUserDashboardSettingsGetResponse,
    IUserDashboardSettingsPostResponse,
    IUserDashboardSettingsPutResponse,
} from "./user-dashboard-settings-response.interface";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { DashboardsService, AuthService, type CurrentUser } from "@shared/index";
import { RoutingParentSyncService } from "@app/routing-utils/routing-parent-sync.service";

interface IUserDashboardSettingsResourceParams {
    dashboardId: string;
    currentUrlPath: string;
}

@Injectable()
export class UserDashboardSettingsService {
    private readonly http = inject(HttpClient);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly authService = inject(AuthService);
    private readonly routingParentSyncService = inject(RoutingParentSyncService);

    UserDashboardSettings: IUserDashboardSettingsModel[];
    UserDashboardSettingsLoadPromise: Promise<IUserDashboardSettingsModel[]> | undefined;
    userDashboardSettingsLoadedEmitter = new Subject();
    userDashboardSettingsLoadedEvent$ = this.userDashboardSettingsLoadedEmitter.asObservable();
    private userDashboardSettingsResource = resource<IUserDashboardSettingsModel[], CurrentUser | null>({
        defaultValue: [],
        params: () => this.authService._currentUser(),
        loader: ({ params: currentUser }: { params: CurrentUser | null }) => {
            if (!currentUser) return Promise.resolve([]);
            return this.getUserDashboardSettings();
        },
    });

    sUserDashboardSettings = computed<IUserDashboardSettingsModel[]>(() => this.userDashboardSettingsResource.value());

    UserDashboardSettingsByDashboardId: IUserDashboardSettingsModel[];
    UserDashboardSettingsForDashboardLoadPromise: Promise<IUserDashboardSettingsModel[]>;
    private currentDashboardId: string;
    private userDashboardSettingsByDashboardIdResource = resource<IUserDashboardSettingsModel[], IUserDashboardSettingsResourceParams>({
        defaultValue: [],
        params: () => ({
            dashboardId: this.dashboardsService.sDashboardId(),
            currentUrlPath: this.routingParentSyncService.currentUrlPath(),
        }),
        loader: ({ params }: { params: IUserDashboardSettingsResourceParams }) => {
            const { dashboardId, currentUrlPath } = params;
            if (!dashboardId || !currentUrlPath || currentUrlPath !== "/settings/dashboardmanagement") return Promise.resolve([]);

            return this.getUserDashboardSettingsForDashboard(dashboardId) as Promise<IUserDashboardSettingsModel[]>;
        },
    });
    sUserDashboardSettingsByDashboardId = computed<IUserDashboardSettingsModel[]>(() => this.userDashboardSettingsByDashboardIdResource.value());

    private currentUserId: string;
    UserDashboardSettingsForMe: IUserDashboardSettingsModel[];
    UserDashboardSettingsForMeLoadPromise: Promise<IUserDashboardSettingsModel[]>;
    private userDashboardSettingsForMeResource = resource<IUserDashboardSettingsModel[], CurrentUser | null>({
        defaultValue: [],
        params: () => this.authService._currentUser(),
        loader: ({ params: currentUser }: { params: CurrentUser | null }) => {
            if (!currentUser) return Promise.resolve([]);
            return this.getUserDashboardSettingsForMe(currentUser.sub);
        },
    });
    sUserDashboardSettingsForMe = computed<IUserDashboardSettingsModel[]>(() => this.userDashboardSettingsForMeResource.value());

    readonly userDashboardSettingsById = computed<IuserDashboardSettingsMapById>(() => {
        const items = this.sUserDashboardSettings();
        if (!items || !items.length) return;

        const map: IuserDashboardSettingsMapById = new Map();
        for (const uds of items) {
            map.set(uds.Id, uds);
        }
        return map;
    });

    readonly userDashboardSettingsByUserAndDashboardId = computed<IUserDashboardSettingsMapByUserAndDashboardId>(() => {
        const items = this.sUserDashboardSettings();
        if (!items || !items.length) return;

        const map: IUserDashboardSettingsMapByUserAndDashboardId = new Map();
        for (const uds of items) {
            map.set(`${uds.UserId}-${uds.DashboardId}`, uds);
        }
        return map;
    });

    readonly userDashboardSettingsByUserId = computed<IUserDashboardSettingsMapByUserId>(() => {
        const items = this.sUserDashboardSettings();
        if (!items || !items.length) return;

        const map: IUserDashboardSettingsMapByUserId = new Map();
        for (const uds of items) {
            let dashboardSettingsByDashboard = map.get(uds.UserId);
            if (!dashboardSettingsByDashboard) dashboardSettingsByDashboard = new Map();

            dashboardSettingsByDashboard.set(uds.DashboardId, uds);
            map.set(uds.UserId, dashboardSettingsByDashboard);
        }
        return map;
    });

    readonly userDashboardSettingsForMe = computed<IUserDashboardSettingsForMe>(() => {
        const items = this.sUserDashboardSettingsForMe();
        if (!items || !items.length) return;

        const map: IUserDashboardSettingsMapByDashboard = new Map();
        for (const uds of items) {
            map.set(uds.DashboardId, uds);
        }
        return map;
    });

    readonly userDashboardSettingsByDashboardId = computed<IUserDashboardSettingsMapByDashboardId>(() => {
        const items = this.sUserDashboardSettingsByDashboardId();
        if (!items || !items.length) return;

        const map: IUserDashboardSettingsMapByDashboardId = new Map();
        for (const uds of items) {
            let dashboardSettingsByUser = map.get(uds.DashboardId);
            if (!dashboardSettingsByUser) dashboardSettingsByUser = new Map();

            dashboardSettingsByUser.set(uds.UserId, uds);
            map.set(uds.DashboardId, dashboardSettingsByUser);
        }
        return map;
    });

    constructor() {}

    async refresh() {
        this.UserDashboardSettingsLoadPromise = undefined;
    }

    async getUserDashboardSettings(forceReload = false): Promise<IUserDashboardSettingsModel[]> {
        if (forceReload && !this.userDashboardSettingsResource.isLoading()) {
            this.UserDashboardSettings = [];
            this.userDashboardSettingsResource.reload();
            this.UserDashboardSettingsLoadPromise = undefined;
        }
        if (!this.UserDashboardSettingsLoadPromise) {
            this.UserDashboardSettingsLoadPromise = this.loadUserDashboardSettings();
        }
        return this.UserDashboardSettingsLoadPromise;
    }

    private async loadUserDashboardSettings(): Promise<IUserDashboardSettingsModel[]> {
        try {
            const result = await firstValueFrom(
                this.http.get<IUserDashboardSettingsGetResponse>(this.getUrl(), {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                }),
            );
            if (result.Code == 200) {
                this.UserDashboardSettings = result.UserDashboardSettings;
                this.userDashboardSettingsLoadedEmitter.next(true);
                return result.UserDashboardSettings;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return [];
        } catch (error) {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return [];
        }
    }

    refreshForDashboard(dashboardId: string, forceReload = false) {
        const _ = this.getUserDashboardSettingsForDashboard(dashboardId, forceReload);
    }

    async getUserDashboardSettingsForDashboard(dashboardId: string, forceReload = false): Promise<IUserDashboardSettingsModel[]> {
        if (forceReload && !this.userDashboardSettingsByDashboardIdResource.isLoading()) {
            this.currentDashboardId = "";
            this.UserDashboardSettingsByDashboardId = [];
            this.userDashboardSettingsByDashboardIdResource.reload();
            this.UserDashboardSettingsForDashboardLoadPromise = undefined;
        }
        if (!this.UserDashboardSettingsForDashboardLoadPromise || this.currentDashboardId !== dashboardId) {
            this.currentDashboardId = dashboardId;
            this.UserDashboardSettingsForDashboardLoadPromise = this.loadUserDashboardSettingsForDashboard(dashboardId);
        }
        return this.UserDashboardSettingsForDashboardLoadPromise;
    }

    private async loadUserDashboardSettingsForDashboard(dashboardId: string): Promise<IUserDashboardSettingsModel[]> {
        try {
            const result = await firstValueFrom(
                this.http.get<IUserDashboardSettingsGetResponse>(this.getUrl() + "dashboardId/" + dashboardId, {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                }),
            );
            if (result.Code == 200) {
                this.UserDashboardSettingsByDashboardId = result.UserDashboardSettings;
                return result.UserDashboardSettings;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return [];
        } catch (error) {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return [];
        }
    }

    async getUserDashboardSettingsForMe(userId: string, forceReload = false): Promise<TUserDashboardSettingsModels> {
        if (forceReload && !this.userDashboardSettingsForMeResource.isLoading()) {
            this.currentUserId = "";
            this.UserDashboardSettingsForMe = [];
            this.userDashboardSettingsForMeResource.reload();
            this.UserDashboardSettingsForMeLoadPromise = undefined;
        }
        if (!this.UserDashboardSettingsForMeLoadPromise || this.currentUserId !== userId) {
            this.currentUserId = userId;
            this.UserDashboardSettingsForMeLoadPromise = this.loadUserDashboardSettingsForMe();
        }
        return this.UserDashboardSettingsForMeLoadPromise;
    }

    private async loadUserDashboardSettingsForMe() {
        try {
            const result = await firstValueFrom(
                this.http.get<IUserDashboardSettingsGetResponse>(this.getUrl() + "me", {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                }),
            );
            if (result.Code == 200) {
                this.UserDashboardSettingsForMe = result.UserDashboardSettings;
                return this.UserDashboardSettingsForMe;
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return [];
        } catch (error) {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
            return [];
        }
    }

    updateUserDashboardSettings(UserDashboardSettingsId: string, userDashboardSettingsData: IUserDashboardSettingsModel) {
        return this.http.put<IUserDashboardSettingsPutResponse>(
            this.getUrl("user-dashboard-settings/" + UserDashboardSettingsId),
            userDashboardSettingsData,
        );
    }

    addUserDashboardSettings(userDashboardSettingsData: IUserDashboardSettingsModel) {
        return this.http.post<IUserDashboardSettingsPostResponse>(this.getUrl(), userDashboardSettingsData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    deleteUserDashboardSettings(UserDashboardSettingsId: string) {
        return this.http.delete<{ Code: number; UserDashboardSetting: string }>(this.getUrl("user-dashboard-settings/" + UserDashboardSettingsId), {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    getUserDashboardSettingsByUserId(userId: string) {
        const uds = this.UserDashboardSettings?.filter((uds) => uds.UserId === userId) ?? [];
        return uds;
    }

    private getUrl(path = "user-dashboard-settings/") {
        return (
            this.appSettingsService.appSettings.UserDashboardSettingsService_URL +
            this.appSettingsService.appSettings.UserDashboardSettingsService_Version +
            path
        );
    }
}
